import { useEffect, useState } from 'react';
import { Archive, ArrowUp, BookOpen, ChevronDown, Gavel, Menu, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const NAV_LINKS = [
  { href: '#about', label: 'About' },
  { href: '#material', label: 'What remains' },
  { href: '#faq', label: 'FAQ' },
] as const;

const ARCHIVE_MATERIAL = [
  {
    icon: BookOpen,
    title: 'Plain-language education',
    description: 'A preserved public-interest project about making everyday legal information easier to approach.',
  },
  {
    icon: Gavel,
    title: 'Malaysia-focused context',
    description: 'Historical material created with Malaysian contracts and common legal questions in mind.',
  },
  {
    icon: ShieldCheck,
    title: 'A careful starting point',
    description: 'Use the archive to orient yourself, then verify current information with official sources and a lawyer.',
  },
] as const;

const ARCHIVE_STEPS = [
  { step: '1', title: 'Read the material', body: 'Explore the preserved public-interest content and its limitations.' },
  { step: '2', title: 'Check official sources', body: 'Use the resources directory to find current government and legal-aid information.' },
  { step: '3', title: 'Get advice when needed', body: 'Speak with a qualified Malaysian lawyer about your specific situation.' },
] as const;

const FAQ_ITEMS = [
  {
    question: 'What is MyLegalAI now?',
    answer: 'MyLegalAI is an archived experimental public-interest project. The remaining website is informational only.',
  },
  {
    question: 'Can I create an account or submit a document?',
    answer: 'No. Accounts, uploads, chat, document analysis, and templates are unavailable, and the interactive service is no longer accepting users.',
  },
  {
    question: 'Is this legal advice?',
    answer: 'No. The archive is educational material only. It does not replace advice from a qualified Malaysian lawyer and does not create a lawyer-client relationship.',
  },
  {
    question: 'Where can I find help?',
    answer: 'Visit the resources page for links to official Malaysian agencies, legal-aid services, courts, tribunals, and emergency contacts. Confirm details with the linked organisation.',
  },
] as const;

const primaryLinkClass =
  'inline-flex min-h-11 items-center justify-center rounded-lg bg-ivory-ink px-6 py-3 font-outfit text-sm font-semibold text-white shadow-sm transition hover:bg-ivory-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/50';

const outlineLinkClass =
  'inline-flex min-h-11 items-center justify-center rounded-lg border border-ivory-border bg-ivory-surface px-6 py-3 font-outfit text-sm font-semibold text-ivory-ink transition hover:bg-ivory-surface-low focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/50';

function Logo() {
  return (
    <span className="inline-flex items-baseline gap-2.5 font-playfair text-[1.65rem] font-bold leading-none tracking-tight text-ivory-ink sm:text-[1.875rem]">
      MyLegal<span className="text-ivory-accent">AI</span>
      <span className="font-outfit text-sm font-bold uppercase tracking-widest text-ivory-accent sm:text-[0.9rem]" aria-label="Malaysia">
        MY
      </span>
    </span>
  );
}

function HeroVisual() {
  return (
    <div className="relative">
      <div className="ivory-shadow overflow-hidden rounded-2xl">
        <img
          src="/hero-tenancy-magnifying.png"
          alt="Illustration of a tenancy agreement under a magnifying glass."
          className="block h-auto w-full"
          loading="eager"
        />
      </div>
      <div className="absolute -right-12 -top-12 -z-10 h-64 w-64 rounded-full bg-ivory-accent/5 blur-3xl" aria-hidden />
      <div className="absolute -bottom-12 -left-12 -z-10 h-48 w-48 rounded-full bg-ivory-accent-dim/20 blur-2xl" aria-hidden />
    </div>
  );
}

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 720);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="ivory-landing min-h-screen overflow-x-hidden bg-ivory-bg font-sans text-ivory-ink antialiased">
      <a href="#about" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-ivory-ink focus:px-4 focus:py-2 focus:text-sm focus:text-white">
        Skip to content
      </a>

      <nav className="fixed top-0 z-50 w-full border-b border-ivory-border bg-ivory-surface/95 shadow-sm shadow-ivory-ink/[0.03] backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-landing items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link to="/" className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/50">
            <Logo />
          </Link>

          <div className="hidden items-center gap-8 md:flex" aria-label="Primary">
            {NAV_LINKS.map(({ href, label }) => (
              <a key={href} href={href} className="rounded-md px-1 py-2 font-outfit text-sm font-semibold tracking-wide text-ivory-ink/80 transition-colors hover:text-ivory-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/50">
                {label}
              </a>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <Link to="/archive" className="hidden rounded-md px-2 py-2 font-outfit text-sm font-semibold text-ivory-ink transition-colors hover:text-ivory-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/50 sm:inline-flex">
              Archive notice
            </Link>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-ivory-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/40 md:hidden" aria-label="Open menu">
                <Menu className="h-6 w-6" aria-hidden />
              </SheetTrigger>
              <SheetContent side="right" className="w-full border-ivory-border bg-ivory-bg sm:max-w-xs">
                <SheetHeader className="border-b border-ivory-border px-6 py-5 text-left">
                  <SheetTitle className="font-playfair text-2xl font-bold text-ivory-ink">
                    MyLegal<span className="text-ivory-accent">AI</span>
                    <span className="ml-2 font-outfit text-sm font-bold uppercase tracking-widest text-ivory-accent">MY</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 px-4 py-4" aria-label="Mobile">
                  {NAV_LINKS.map(({ href, label }) => (
                    <a key={href} href={href} className="rounded-md px-3 py-3 font-outfit text-base font-semibold text-ivory-muted hover:text-ivory-ink" onClick={() => setMobileMenuOpen(false)}>
                      {label}
                    </a>
                  ))}
                  <Link to="/resources" className="rounded-md px-3 py-3 font-outfit text-base font-semibold text-ivory-muted hover:text-ivory-ink" onClick={() => setMobileMenuOpen(false)}>
                    Resources
                  </Link>
                  <Link to="/archive" className={`${primaryLinkClass} mt-3 w-full`} onClick={() => setMobileMenuOpen(false)}>
                    Archive notice
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <main className="pt-20">
        <section id="about" className="relative scroll-mt-24 overflow-hidden bg-ivory-bg px-4 py-14 sm:px-6 sm:py-20 lg:px-12 lg:py-28">
          <div className="mx-auto grid w-full max-w-landing grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="flex flex-col">
              <span className="mb-5 inline-block w-fit rounded-full border border-ivory-border bg-ivory-surface px-3 py-1.5 font-outfit text-xs font-semibold uppercase tracking-widest text-ivory-ink">
                Malaysia · Public interest · Archive
              </span>
              <h1 className="mb-6 font-playfair text-4xl font-bold leading-[1.1] tracking-tight text-ivory-ink sm:text-5xl lg:text-[64px]">
                Legal clarity, <span className="italic text-ivory-accent">preserved.</span>
              </h1>
              <p className="mb-8 max-w-xl text-lg leading-relaxed text-ivory-muted sm:text-xl">
                MyLegalAI was an experimental public-interest project about making everyday legal information easier to understand.
              </p>
              <div className="rounded-2xl border border-ivory-accent/30 bg-ivory-surface p-5 shadow-sm sm:p-6">
                <div className="flex gap-3">
                  <Archive className="mt-1 h-5 w-5 shrink-0 text-ivory-accent" aria-hidden />
                  <p className="text-sm leading-relaxed text-ivory-muted sm:text-base">
                    The interactive AI service is no longer accepting users. Accounts, uploads, chat, document analysis, and templates are unavailable.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/archive" className={primaryLinkClass}>Read the archive notice</Link>
                <Link to="/resources" className={outlineLinkClass}>Find official resources</Link>
              </div>
            </div>
            <HeroVisual />
          </div>
        </section>

        <section id="material" className="scroll-mt-20 border-y border-ivory-border/50 bg-ivory-surface px-4 py-20 sm:px-6 lg:px-12">
          <div className="mx-auto max-w-landing">
            <div className="mb-12 max-w-2xl">
              <p className="mb-3 font-outfit text-xs font-semibold uppercase tracking-[0.2em] text-ivory-accent">What remains</p>
              <h2 className="font-playfair text-3xl font-semibold text-ivory-ink sm:text-4xl">A small, honest public archive.</h2>
              <p className="mt-4 text-base leading-relaxed text-ivory-muted">The remaining material is informational only. Check current official sources before relying on it.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {ARCHIVE_MATERIAL.map(({ icon: Icon, title, description }) => (
                <div key={title} className="ivory-shadow rounded-xl border border-ivory-border/70 bg-ivory-bg p-6 sm:p-8">
                  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-lg bg-ivory-surface-high text-ivory-accent">
                    <Icon className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className="mb-3 font-playfair text-xl font-semibold text-ivory-ink">{title}</h3>
                  <p className="text-sm leading-relaxed text-ivory-muted sm:text-base">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="scroll-mt-20 bg-ivory-surface-low px-4 py-20 sm:px-6 lg:px-12">
          <div className="mx-auto max-w-landing">
            <h2 className="mb-12 font-playfair text-3xl font-semibold text-ivory-ink sm:text-4xl">How to use this archive</h2>
            <ol className="m-0 grid list-none gap-6 p-0 md:grid-cols-3">
              {ARCHIVE_STEPS.map(({ step, title, body }) => (
                <li key={step} className="ivory-shadow rounded-xl border border-ivory-border/60 bg-ivory-surface p-8">
                  <span className="mb-5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-ivory-surface-high font-outfit text-sm font-semibold text-ivory-accent ring-1 ring-ivory-border">{step}</span>
                  <h3 className="mb-3 font-playfair text-xl font-semibold text-ivory-ink">{title}</h3>
                  <p className="text-sm leading-relaxed text-ivory-muted">{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="faq" className="scroll-mt-20 bg-ivory-bg px-4 py-20 sm:px-6 lg:px-12">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-8 font-playfair text-3xl font-semibold text-ivory-ink sm:text-4xl">Common questions</h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map(({ question, answer }) => (
                <details key={question} className="group rounded-xl border border-ivory-border bg-ivory-surface transition-colors open:border-ivory-accent/30 open:bg-ivory-surface-low">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-xl px-5 py-4 text-sm font-medium text-ivory-ink marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/40 [&::-webkit-details-marker]:hidden">
                    {question}
                    <ChevronDown className="h-4 w-4 shrink-0 text-ivory-muted transition-transform group-open:rotate-180" aria-hidden />
                  </summary>
                  <p className="px-5 pb-4 text-sm leading-relaxed text-ivory-muted">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-ivory-border/50 px-4 py-16 sm:px-6 lg:px-12">
          <div className="mx-auto max-w-landing rounded-3xl bg-ivory-container p-8 text-center sm:p-14">
            <h2 className="font-playfair text-3xl font-semibold text-ivory-ink sm:text-[40px]">Keep the important questions human.</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-ivory-muted">
              Remaining material is informational only. It is not legal advice and creates no lawyer-client relationship.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/resources" className={primaryLinkClass}>View resources</Link>
              <Link to="/terms" className={outlineLinkClass}>Read the disclaimer</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-ivory-border bg-ivory-container py-14">
        <div className="mx-auto grid max-w-landing gap-10 px-4 sm:px-6 md:grid-cols-3 lg:px-12">
          <div className="space-y-4">
            <Link to="/" className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/50"><Logo /></Link>
            <p className="max-w-xs text-sm leading-relaxed text-ivory-muted">An archived experimental public-interest project. Not a law firm.</p>
          </div>
          <div>
            <h4 className="mb-4 font-outfit text-sm font-semibold uppercase tracking-wider text-ivory-ink">Archive</h4>
            <ul className="space-y-2 text-sm text-ivory-muted">
              <li><Link to="/archive" className="inline-flex min-h-11 items-center hover:text-ivory-accent">Archive notice</Link></li>
              <li><Link to="/resources" className="inline-flex min-h-11 items-center hover:text-ivory-accent">Official resources</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-outfit text-sm font-semibold uppercase tracking-wider text-ivory-ink">Legal</h4>
            <ul className="space-y-2 text-sm text-ivory-muted">
              <li><Link to="/privacy" className="inline-flex min-h-11 items-center hover:text-ivory-accent">Privacy policy</Link></li>
              <li><Link to="/terms" className="inline-flex min-h-11 items-center hover:text-ivory-accent">Terms and disclaimer</Link></li>
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-10 flex max-w-landing flex-col gap-3 border-t border-ivory-border/30 px-4 pt-8 text-xs text-ivory-muted sm:px-6 md:flex-row md:items-center md:justify-between lg:px-12">
          <p>© 2026 MyLegalAI. Informational archive only.</p>
          <p className="text-pretty md:text-right">Consult a qualified lawyer for advice about your situation.</p>
        </div>
      </footer>

      <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={cn('fixed bottom-8 right-8 z-30 hidden h-11 w-11 items-center justify-center rounded-full border border-ivory-border bg-ivory-surface text-ivory-ink shadow-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ivory-accent/40 lg:flex', showBackToTop ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0')} aria-label="Back to top">
        <ArrowUp className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
